import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function useReferenceSelection(
  comparisonOptions: string[],
  urlReference: string | undefined
): {
  selectedReferences: string[];
  referenceContext: string | undefined;
  handlePrimarySelect: (value: string | null) => void;
  handleToggleReference: (reference: string) => void;
  getResetRefsCallback: () => () => void;
} {
  const navigate = useNavigate();
  const [selectedReferences, setSelectedReferences] = useState<string[]>([]);

  const preservedSelectionRef = useRef<string[]>([]);
  const isInitialLoadRef = useRef<boolean>(true);
  const urlReferenceProcessedRef = useRef<boolean>(false);
  const preservedAppliedRef = useRef<boolean>(false);

  // Handle URL parameter reference (only on initial load)
  useEffect(() => {
    if (urlReference && !urlReferenceProcessedRef.current) {
      try {
        const decodedReference = decodeURIComponent(urlReference);
        if (decodedReference && decodedReference.trim()) {
          preservedSelectionRef.current = [decodedReference];
          urlReferenceProcessedRef.current = true;
        }
      } catch (error) {
        console.error('Failed to decode reference parameter:', error);
      }
    }
  }, [urlReference]);

  // Auto-select initial references
  useEffect(() => {
    if (!comparisonOptions.length) {
      return;
    }

    setSelectedReferences((current) => {
      const preserved = preservedSelectionRef.current.filter((item) =>
        comparisonOptions.includes(item)
      );

      const sanitized = current.filter((item) => comparisonOptions.includes(item));
      let next = preserved.length ? preserved : sanitized;

      if (!next.length) {
        next = comparisonOptions.slice(0, Math.min(2, comparisonOptions.length));
      }

      if (
        next.length === current.length &&
        next.every((value, index) => value === current[index])
      ) {
        return current;
      }

      return next;
    });
  }, [comparisonOptions]);

  // Clear preserved ref once selectedReferences actually has the preserved value
  useEffect(() => {
    const preserved = preservedSelectionRef.current;
    if (preserved.length > 0 && selectedReferences.length > 0) {
      const isApplied = preserved.every((ref) => selectedReferences.includes(ref));
      if (isApplied) {
        preservedSelectionRef.current = [];
        preservedAppliedRef.current = true;
      }
    }
  }, [selectedReferences]);

  // Sync URL with primary reference (first selected reference)
  useEffect(() => {
    if (isInitialLoadRef.current) {
      if (selectedReferences.length > 0) {
        isInitialLoadRef.current = false;
      }
      return;
    }

    const primaryReference = selectedReferences[0];

    if (!primaryReference) {
      if (urlReference) {
        navigate('/analytics/comparison', { replace: true });
      }
      return;
    }

    let currentUrlReference: string | undefined;
    try {
      currentUrlReference = urlReference ? decodeURIComponent(urlReference) : undefined;
    } catch (error) {
      console.error('Failed to decode URL reference:', error);
      currentUrlReference = urlReference;
    }

    if (primaryReference !== currentUrlReference) {
      const encodedReference = encodeURIComponent(primaryReference);
      navigate(`/analytics/comparison/${encodedReference}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReferences, urlReference]);

  const handlePrimarySelect = useCallback(
    (value: string | null) => {
      if (!value || !comparisonOptions.includes(value)) {
        return;
      }

      setSelectedReferences((current) => {
        const filtered = current.filter((item) => item !== value);
        const next = [value, ...filtered];
        if (
          next.length === current.length &&
          next.every((item, index) => item === current[index])
        ) {
          return current;
        }
        return next;
      });
    },
    [comparisonOptions]
  );

  const handleToggleReference = useCallback(
    (reference: string) => {
      if (!comparisonOptions.includes(reference)) {
        return;
      }

      setSelectedReferences((current) => {
        const exists = current.includes(reference);
        if (exists) {
          if (current.length === 1) {
            return current;
          }

          const filtered = current.filter((item) => item !== reference);
          if (current[0] === reference) {
            const [newPrimary, ...rest] = filtered;
            const next = newPrimary ? [newPrimary, ...rest] : filtered;
            return next;
          }
          return filtered;
        }

        return [...current, reference];
      });
    },
    [comparisonOptions]
  );

  const getResetRefsCallback = useCallback(() => {
    return () => {
      urlReferenceProcessedRef.current = false;
      preservedAppliedRef.current = false;
      preservedSelectionRef.current = [];
      isInitialLoadRef.current = true;
    };
  }, []);

  const referenceContext = useMemo(() => {
    return selectedReferences[0] || undefined;
  }, [selectedReferences]);

  return {
    selectedReferences,
    referenceContext,
    handlePrimarySelect,
    handleToggleReference,
    getResetRefsCallback,
  };
}
